import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { SidebarNavLink, SidebarWrapper } from '@components';
import { GlobalContext } from '@contexts';
import { isPrimarySite } from '@helpers';
import { datamodelsQuery, themeQuery } from '@queries';
import {
  ArrowLeftIcon,
  BallIcon,
  ClipboardIcon,
  EditIcon,
  MapIcon,
  MapPinIcon,
  PageIcon,
  SettingsIcon,
  UsersIcon,
  ListIcon,
  LayersIcon,
} from '@svgs';

const SiteSidebar = (props) => {
  const globals = useContext(GlobalContext);

  // get datamodels
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      datamodel_type: 'site_custom_fields',
    }),
  );

  // fetch theme
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(props.siteData?.theme_id, {
      enabled: !!props?.siteData?.theme_id,
    }),
  );

  // datamodel ID for site custom fields
  const dataModelID = useMemo(() => {
    const themeDatamodelId =
      !themeIsPending && themeData?.datamodel_id
        ? themeData.datamodel_id
        : null;

    const fallbackDatamodelId =
      !datamodelsIsPending && datamodelsData?.objects?.length > 0
        ? datamodelsData.objects[0].id
        : null;

    return themeDatamodelId ?? fallbackDatamodelId;
  }, [datamodelsData, datamodelsIsPending, themeData, themeIsPending]);

  // determine if the manage content link should be shown
  const shouldShowManagContent = useMemo(() => {
    return (
      // if the site is not primary and there is a data model ID, show the manage content link
      (dataModelID && props.siteData && !isPrimarySite(props.siteData)) ||
      // if the site is primary and there is a theme data model ID, show the manage content link
      (themeData?.datamodel_id && isPrimarySite(props.siteData))
    );
  }, [dataModelID, props.siteData, themeData, themeIsPending]);

  // determine if the menus link should be shown
  const shouldShowMenus = useMemo(() => {
    if (!themeData) return false;
    // any site overrides set
    const siteOverrideKeys = Object.keys(themeData?.site_override || {});
    return (
      props.siteData &&
      (siteOverrideKeys.length === 0 ||
        (siteOverrideKeys.length > 0 &&
          !themeData.site_override_objects.includes('linklists')))
    );
  }, [props.siteData, themeData]);

  return (
    <SidebarWrapper>
      <div className="sb-sidebar-nav">
        <div className="sb-sidebar-header">
          {!props.siteData && (
            <a className="sb-sidebar-back-btn sb-flex">...</a>
          )}
          {props.siteData && (
            <Link
              to={`/dashboard/projects/${props.siteData.business_id}/sites`}
              className="sb-sidebar-back-btn sb-flex"
            >
              <ArrowLeftIcon />
              Back to Project
            </Link>
          )}
        </div>
        <ul>
          <li className="sb-sidebar-nav-title">Website</li>
          {shouldShowManagContent && (
            <SidebarNavLink
              to={`/dashboard/sites/${props.siteID}/custom_fields`}
              tooltip="Update the content of this Website's homepage and subpages"
            >
              <ClipboardIcon />
              Manage content
            </SidebarNavLink>
          )}
          {globals.visible.sitePagesView && (
            <SidebarNavLink
              to={`/dashboard/sites/${props.siteID}/pages`}
              tooltip="Manage the pages within this Website"
            >
              <PageIcon />
              Pages
            </SidebarNavLink>
          )}
          {globals.visible.siteBlogsView && (
            <SidebarNavLink
              to={`/dashboard/sites/${props.siteID}/blogs`}
              tooltip="Manage blog posts and categories for this Website"
            >
              <EditIcon />
              Blogs
            </SidebarNavLink>
          )}
          {shouldShowMenus && (
            <SidebarNavLink to={`/dashboard/sites/${props.siteID}/menus`}>
              <ListIcon />
              Menus
            </SidebarNavLink>
          )}
          {globals.visible.siteSEOView &&
            props.siteData &&
            isPrimarySite(props.siteData) && (
              <SidebarNavLink to={`/dashboard/sites/${props.siteID}/SEO`}>
                <LayersIcon />
                AI/SEO
              </SidebarNavLink>
            )}
          {(globals.visible.siteLocationView ||
            globals.visible.locationSocialView ||
            globals.visible.locationTerritoriesView) &&
            props.siteData &&
            props.siteData.locations.length > 0 && (
              <>
                <li className="sb-sidebar-nav-title">Location details</li>
                {globals.visible.siteLocationView && (
                  <SidebarNavLink
                    to={`/dashboard/sites/${props.siteID}/location_details`}
                    tooltip="Update the Location details including name, address, and phone numbers"
                  >
                    <MapPinIcon />
                    Basic information
                  </SidebarNavLink>
                )}
                {/**<SidebarNavLink to={`/dashboard/sites/${props.siteID}/hours`}>
                <ClockIcon/>Hours of operation
              </SidebarNavLink>**/}
                {globals.visible.locationTerritoriesView && (
                  <SidebarNavLink
                    to={`/dashboard/sites/${props.siteID}/territories`}
                  >
                    <MapIcon />
                    Territories
                  </SidebarNavLink>
                )}
                {globals.visible.locationSocialView && (
                  <SidebarNavLink
                    to={`/dashboard/sites/${props.siteID}/social`}
                    tooltip="Update the links to social profiles"
                  >
                    <BallIcon />
                    Social media
                  </SidebarNavLink>
                )}
                {/**<SidebarNavLink to={`/dashboard/sites/${props.siteID}/seo`}>
                <SearchIcon/>Local SEO
              </SidebarNavLink>**/}
              </>
            )}
          {globals.visible.projectTemplatesView &&
            props.siteData &&
            isPrimarySite(props.siteData) && (
              <>
                <li className="sb-sidebar-nav-title">Template</li>
                <SidebarNavLink
                  to={`/dashboard/projects/${props.siteData.business_id}/templates/${props.siteID}/pages`}
                  tooltip="Enter template editing mode for this Website"
                >
                  <MapPinIcon />
                  Manage template
                </SidebarNavLink>
              </>
            )}
        </ul>
      </div>
      <ul className="sb-sidebar-footer">
        {globals.visible.siteSettingsView && (
          <SidebarNavLink
            to={`/dashboard/sites/${props.siteID}/settings`}
            tooltip="Update the settings for this Website including published URL"
          >
            <SettingsIcon />
            Website settings
          </SidebarNavLink>
        )}
        {globals.visible.siteAccessView &&
          props.siteData &&
          props.siteData.locations.length > 0 && (
            <SidebarNavLink
              to={`/dashboard/sites/${props.siteID}/access`}
              tooltip="Grant and revoke user login access to this Website"
            >
              <UsersIcon />
              Manage access
            </SidebarNavLink>
          )}
      </ul>
    </SidebarWrapper>
  );
};

export default SiteSidebar;
